import { Component, ElementRef, EventEmitter, inject, Output, TemplateRef, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { SaleItem } from 'src/app/models/sale-item.model';
import { Sale } from 'src/app/models/sale.model';
import { Stock } from 'src/app/models/stock';
import { InventoryService } from 'src/app/services/inventory.service';
import { STORAGE_KEYS, StorageService } from 'src/app/services/storage.service';
import { IconsService } from 'src/app/shared/services/icons.service';

@Component({
  selector: 'app-modal-sales-order',
  template: `
    <ng-template #modalSalesOrder let-modal>
      <div class="modal-header">
        <h4 class="modal-title" id="modal-basic-title">modal-sales-order works!</h4>
        <button type="button" class="close" aria-label="Close" (click)="modal.dismiss('Cross click')">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <div class="modal-body">
        <form [formGroup]="saleForm" (ngSubmit)="saveSale()">
          <!-- Sale Notes -->
          <div class="form-group">
            <label for="notes">Notas</label>
            <textarea 
              class="form-control" 
              id="notes" 
              formControlName="notes" 
              rows="2">
            </textarea>
          </div>

          <!-- Line Items -->
          <div class="mt-4">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h5 class="mb-0">Productos</h5>
              <button 
                type="button" 
                class="btn btn-sm btn-primary"
                (click)="addLineItem()">
                <fa-icon [icon]="icons.faPlus"></fa-icon> Agregar Producto
              </button>
            </div>

            <div formArrayName="items">
              <div *ngFor="let item of items.controls; let i=index" 
                   [formGroupName]="i"
                   class="card mb-3">
                <div class="card-body">
                  <div class="row">
                    <!-- Stock Selection -->
                    <div class="col-md-4">
                      <div class="form-group">
                        <label>Producto</label>
                        <select 
                          class="form-control" 
                          formControlName="stock_id"
                          (change)="onStockSelect($event, i)">
                          <option value="">Seleccionar Producto</option>
                          <option *ngFor="let stock of availableStock" 
                                  [value]="stock.id">
                            {{stock.name}} ({{stock.sku}})
                          </option>
                        </select>
                      </div>
                    </div>

                    <!-- Quantity -->
                    <div class="form-group">
                      <label>Cantidad</label>
                      <input 
                        type="number" 
                        class="form-control" 
                        formControlName="quantity"
                        (input)="updateLineItemTotal(i)"
                        [min]="1"
                        [max]="getAvailableQuantity(item)">
                      <small class="text-muted" *ngIf="item.get('stock_id')?.value">
                        Disponible: {{ getStockQuantity(item.get('stock_id')?.value) }}
                      </small>
                      <div class="invalid-feedback" *ngIf="item.get('quantity')?.errors?.['exceededStock']">
                        Cantidad excede el stock disponible
                      </div>
                    </div>

                    <!-- Unit Price -->
                    <div class="col-md-3">
                      <div class="form-group">
                        <label>Precio Unitario</label>
                        <input 
                          type="number" 
                          class="form-control" 
                          formControlName="unit_price"
                          (input)="updateLineItemTotal(i)"
                          min="0"
                          step="0.01">
                      </div>
                    </div>

                    <!-- Total -->
                    <div class="col-md-3">
                      <div class="form-group">
                        <label>Total</label>
                        <input 
                          type="number" 
                          class="form-control" 
                          formControlName="total"
                          readonly>
                      </div>
                    </div>

                    <!-- Remove Button -->
                    <div class="col-md-1 d-flex align-items-center mt-3">
                      <button 
                        type="button" 
                        class="btn btn-danger btn-sm"
                        (click)="removeLineItem(i)">
                        <fa-icon [icon]="icons.faTrashAlt"></fa-icon>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Order Total -->
          <div class="form-group text-end mt-4">
            <h5>Total: {{orderTotal | currency}}</h5>
          </div>

          <!-- Submit Button -->
          <div class="d-flex justify-content-end mt-4">
            <button 
              type="button" 
              class="btn btn-secondary me-2" 
              (click)="dismiss()">Cancelar</button>
            <button 
              type="submit" 
              class="btn btn-primary"
              [disabled]="!saleForm.valid || items.length === 0">
              Guardar Venta
            </button>
          </div>
        </form>
      </div>
    </ng-template>
  `,
  styles: [
  ]
})
export class ModalSalesOrderComponent {
  @Output() updateDismiss: EventEmitter<any> = new EventEmitter<any>();
  @ViewChild('modalSalesOrder') modalSalesOrder !: ElementRef;

  private modalRef: NgbModalRef | undefined;
  saleForm: FormGroup = new FormGroup({});
  availableStock: Stock[] = [];
  orderTotal: number = 0;
  storageService = inject(StorageService);
  constructor(
    private modalService: NgbModal,
    private fb: FormBuilder,
    private inventoryService: InventoryService,
    private toastr: ToastrService,
    public icons: IconsService
  ) {
    this.initForm();
    this.loadStock();
  }

  getStockQuantity(stockId: string): number {
    const stock = this.availableStock.find((s: any) => s.id === stockId);
    return stock?.quantity || 0;
  }

  getAvailableQuantity(item: AbstractControl): number {
    const stockId = item.get('stock_id')?.value;
    const stock = this.availableStock.find(s => s.id === stockId);
    return stock?.quantity || 0;
  }

  get items() {
    return this.saleForm.get('items') as FormArray;
  }

  private initForm() {
    this.saleForm = this.fb.group({
      notes: [''],
      items: this.fb.array([])
    });
  }

  private validateStockQuantity(stockId: string, requestedQuantity: number): boolean {
    const stock = this.availableStock.find((s: any) => s.id === stockId);
    if (!stock) return false;
    return stock?.quantity! >= requestedQuantity;
  }

  private loadStock() {
    this.inventoryService.getStock({ page: 1, per_page: 1000}).subscribe({
      next: (response) => {
        this.availableStock = response.data;
      },
      error: (error) => {
        this.toastr.error('Error loading stock items');
      }
    });
  }

  addLineItem() {
    const itemGroup = this.fb.group({
      stock_id: ['', Validators.required],
      quantity: [1, [
        Validators.required, 
        Validators.min(1),
        // Add custom validator
        (control: AbstractControl) => {
          if (!control.parent) return null;
          const stockId = control.parent.get('stock_id')?.value;
          if (!stockId) return null;
          
          const isValid = this.validateStockQuantity(stockId, control.value);
          return isValid ? null : { exceededStock: true };
        }
      ]],
      unit_price: [0, [Validators.required, Validators.min(0)]],
      total: [{value: 0, disabled: true}]
    });

    this.items.push(itemGroup);
  }

  removeLineItem(index: number) {
    this.items.removeAt(index);
    this.calculateOrderTotal();
  }

  onStockSelect(event: Event, index: number) {
    const stockId = (event.target as HTMLSelectElement).value;
    const stock = this.availableStock.find((s: any) => s.id === stockId);
    
    if (stock) {
      const item = this.items.at(index);
      item.patchValue({
        unit_price: stock.unit_price || 0,
        quantity: 1 // Reset quantity when stock changes
      });
      
      // Update the quantity input max attribute
      const quantityInput = document.querySelector(`[formGroupName="${index}"] input[formControlName="quantity"]`);
      if (quantityInput) {
        (quantityInput as HTMLInputElement).max = stock.quantity!.toString();
      }
      
      this.updateLineItemTotal(index);
    }
  }

  updateLineItemTotal(index: number) {
    const item = this.items.at(index);
    const quantity = item.get('quantity')?.value || 0;
    const stockId = item.get('stock_id')?.value;
    const unitPrice = item.get('unit_price')?.value || 0;
    
    if (stockId && !this.validateStockQuantity(stockId, quantity)) {
      const stock = this.availableStock.find(s => s.id === stockId);
      this.toastr.error(`Solo hay ${stock?.quantity} unidades disponibles`);
      item.patchValue({ quantity: stock?.quantity || 0 });
      return;
    }

    const total = quantity * unitPrice;
    item.patchValue({ total }, { emitEvent: false });
    this.calculateOrderTotal();
  }

  calculateOrderTotal() {
    this.orderTotal = this.items.controls.reduce((sum, item) => {
      return sum + (item.get('total')?.value || 0);
    }, 0);
  }

  saveSale() {
    if (this.saleForm.valid) {
      const invalidItems = this.items.controls.filter(item => {
        const stockId = item.get('stock_id')?.value;
        const quantity = item.get('quantity')?.value;
        return !this.validateStockQuantity(stockId, quantity);
      });

      if (invalidItems.length > 0) {
        this.toastr.error('Algunos productos exceden el stock disponible');
        return;
      }

      const saleData: Sale = {
        notes: this.saleForm.get('notes')?.value,
        total_amount: this.orderTotal,
        status: 0, // Pending
        created_by_id: (this.storageService.getItem(STORAGE_KEYS.USER) ? this.storageService.getItem(STORAGE_KEYS.USER) : {}).id
      };

      const items: SaleItem[] = this.items.controls.map(item => ({
        stock_id: item.get('stock_id')?.value,
        quantity: item.get('quantity')?.value,
        unit_price: item.get('unit_price')?.value,
        total: item.get('total')?.value
      }));

      // Add createSale method to InventoryService
      this.inventoryService.createSale({ ...saleData, items }).subscribe({
        next: (response) => {
          this.toastr.success('Venta creada exitosamente');
          this.updateDismiss.emit(response);
          this.dismiss();
        },
        error: (error) => {
          this.toastr.error('Error al crear la venta');
        }
      });
    }
  }

  openModal(modalContent: TemplateRef<any>) {
    this.modalRef = this.modalService.open(modalContent, { size: 'lg' });
  }

  dismiss() {
    if (this.modalRef) {
      this.modalRef.dismiss();
    }
  }
}
